import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import PageShell from '../Components/ui/PageShell';
import RequirementDateRangeFilter from '../Components/ui/RequirementDateRangeFilter';
import SourceProfilesList from '../Components/requirements/SourceProfilesList';
import CandidateProfileModal from '../Components/tracking/CandidateProfileModal';
import { formatDisplayDate } from '../utils/dateFormat';
import {
  getDefaultProfilesWeekRange,
  getTodayProfilesRange,
  getYesterdayProfilesRange,
  matchesDefaultProfilesWeekRange,
  matchesTodayProfilesRange,
  matchesYesterdayProfilesRange,
} from '../utils/profilesQuickDateRange';
import { normalizeRecruiterCandidateGroups } from '../utils/candidateListState';

const INITIAL_RANGE = getTodayProfilesRange();

function SourceProfilesViewPage({
  fetchProfiles,
  backTo = '/dashboard',
  subtitle = 'Browse uploaded source profiles for a selected date range.',
}) {
  const [draftFromDate, setDraftFromDate] = useState(INITIAL_RANGE.from);
  const [draftToDate, setDraftToDate] = useState(INITIAL_RANGE.to);
  const [appliedFromDate, setAppliedFromDate] = useState(INITIAL_RANGE.from);
  const [appliedToDate, setAppliedToDate] = useState(INITIAL_RANGE.to);
  const [profilesPeriod, setProfilesPeriod] = useState('today');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [recruitersData, setRecruitersData] = useState([]);
  const [total, setTotal] = useState(0);
  const [profileCandidateId, setProfileCandidateId] = useState('');
  const [showProfileModal, setShowProfileModal] = useState(false);

  const loadProfiles = useCallback(async (from, to) => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await fetchProfiles({ from, to });
      setRecruitersData(normalizeRecruiterCandidateGroups(response.recruiters || []));
      setTotal(response.total ?? 0);
    } catch (error) {
      console.error('Profiles view load failed:', error);
      setLoadError(error.message || 'Failed to load source profiles.');
      setRecruitersData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [fetchProfiles]);

  useEffect(() => {
    loadProfiles(appliedFromDate, appliedToDate);
  }, [appliedFromDate, appliedToDate, loadProfiles]);

  const applyQuickRange = (range, period) => {
    setProfilesPeriod(period);
    setDraftFromDate(range.from);
    setDraftToDate(range.to);
    setAppliedFromDate(range.from);
    setAppliedToDate(range.to);
  };

  const selectProfilesWeek = () => {
    applyQuickRange(getDefaultProfilesWeekRange(), 'week');
  };

  const selectProfilesYesterday = () => {
    applyQuickRange(getYesterdayProfilesRange(), 'yesterday');
  };

  const selectProfilesToday = () => {
    applyQuickRange(getTodayProfilesRange(), 'today');
  };

  const applyDateFilters = () => {
    setProfilesPeriod('');
    setAppliedFromDate(draftFromDate);
    setAppliedToDate(draftToDate);
  };

  const clearDateFilters = () => {
    applyQuickRange(getTodayProfilesRange(), 'today');
  };

  const periodHint = useMemo(() => {
    if (matchesTodayProfilesRange(appliedFromDate, appliedToDate)) {
      return `Showing profiles sourced today (${formatDisplayDate(appliedFromDate)}).`;
    }
    if (matchesYesterdayProfilesRange(appliedFromDate, appliedToDate)) {
      return `Showing profiles sourced yesterday (${formatDisplayDate(appliedFromDate)}).`;
    }
    if (matchesDefaultProfilesWeekRange(appliedFromDate, appliedToDate)) {
      return `Showing profiles sourced in the last 1 week (${formatDisplayDate(appliedFromDate)} – ${formatDisplayDate(appliedToDate)}).`;
    }
    return `Showing profiles sourced (${formatDisplayDate(appliedFromDate)} – ${formatDisplayDate(appliedToDate)}).`;
  }, [appliedFromDate, appliedToDate]);

  const handleViewCandidate = (candidateId) => {
    setProfileCandidateId(candidateId);
    setShowProfileModal(true);
  };

  const handleProfileUpdated = () => {
    loadProfiles(appliedFromDate, appliedToDate);
  };

  return (
    <PageShell
      title="Profiles View"
      subtitle={subtitle}
      breadcrumbs={[
        { label: 'Dashboard', path: '/dashboard' },
        { label: 'Profiles View' },
      ]}
      backTo={backTo}
    >
      <div className="hr-employee-work-summary-period-actions mb-3">
        <Button
          type="button"
          variant={profilesPeriod === 'week' ? 'primary' : 'outline-primary'}
          className="hr-employee-work-summary-period-btn"
          onClick={selectProfilesWeek}
          disabled={loading}
        >
          1 Week
        </Button>
        <Button
          type="button"
          variant={profilesPeriod === 'yesterday' ? 'primary' : 'outline-primary'}
          className="hr-employee-work-summary-period-btn"
          onClick={selectProfilesYesterday}
          disabled={loading}
        >
          Yesterday
        </Button>
        <Button
          type="button"
          variant={profilesPeriod === 'today' ? 'primary' : 'outline-primary'}
          className="hr-employee-work-summary-period-btn"
          onClick={selectProfilesToday}
          disabled={loading}
        >
          Today
        </Button>
      </div>

      <section className="hr-card hr-rtl-req-filters-card mb-3">
        <div className="hr-rtl-req-filter-toolbar">
          <div className="hr-rtl-req-filter-group">
            <span className="hr-rtl-req-filter-label">Date Range</span>
            <RequirementDateRangeFilter
              fromDate={draftFromDate}
              toDate={draftToDate}
              onFromChange={(value) => {
                setDraftFromDate(value);
                setProfilesPeriod('');
              }}
              onToChange={(value) => {
                setDraftToDate(value);
                setProfilesPeriod('');
              }}
              inline
              defaultFromDate={INITIAL_RANGE.from}
              defaultToDate={INITIAL_RANGE.to}
              hideClearButton
              className="hr-rtl-req-date-range"
            />
          </div>
          <div className="hr-rtl-req-profiles-filter-actions">
            <button
              type="button"
              className="hr-rtl-req-filter-search-btn"
              onClick={applyDateFilters}
              disabled={loading}
            >
              Apply
            </button>
            <button
              type="button"
              className="hr-rtl-req-filter-clear-btn"
              onClick={clearDateFilters}
              disabled={loading}
            >
              Clear
            </button>
          </div>
        </div>
      </section>

      {!loading && (
        <p className="hr-page-hint mb-3">{periodHint}</p>
      )}

      {loading && (
        <p className="hr-page-hint mb-3">Loading source profiles…</p>
      )}

      {loadError && (
        <div className="alert alert-danger">{loadError}</div>
      )}

      {!loading && !loadError && (
        <p className="hr-page-hint mb-3">
          Total profiles sourced in selected scope:
          {' '}
          <strong>{total}</strong>
        </p>
      )}

      {!loading && !loadError && (
        <section className="hr-card">
          <SourceProfilesList
            recruitersData={recruitersData}
            onViewCandidate={handleViewCandidate}
            resetKey={`${appliedFromDate}-${appliedToDate}`}
            showClientColumn
          />
        </section>
      )}

      <CandidateProfileModal
        show={showProfileModal}
        onHide={() => setShowProfileModal(false)}
        candidateId={profileCandidateId}
        onUpdated={handleProfileUpdated}
      />
    </PageShell>
  );
}

export default SourceProfilesViewPage;
